import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { MediaTile } from "@/components/MediaTile";
import { useProducts } from "@/hooks/useProducts";

const AUTOPLAY_INTERVAL_MS = 4000;

/**
 * Wide, "channel-art" style advertisement banner shown near the top of the
 * homepage. Fully admin-controlled: whatever the admin uploads under the
 * "Homepage Banner" category (cover photo + up to 3 gallery photos) is
 * shown here, auto-rotating. Clicking anywhere on the banner takes the
 * visitor to the New Arrivals page. Renders nothing if the admin hasn't
 * configured a banner yet, so it never leaves an empty gap.
 */
export const AdvertisementBanner = () => {
  const { items: banners, loading } = useProducts("banner");
  const navigate = useNavigate();
  const [api, setApi] = useState(null);

  const banner = banners?.[0];
  const images = useMemo(() => {
    if (!banner) return [];
    const list = [];
    if (banner.media_url) list.push(banner.media_url);
    (banner.gallery_images || []).forEach((url) => list.push(url));
    return list.slice(0, 3);
  }, [banner]);

  useEffect(() => {
    if (!api || images.length <= 1) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, AUTOPLAY_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [api, images.length]);

  if (loading || images.length === 0) return null;

  return (
    <section
      className="relative w-full aspect-[3/1] sm:aspect-[4/1] lg:aspect-[6/1] overflow-hidden cursor-pointer group"
      onClick={() => navigate("/new-arrivals")}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") navigate("/new-arrivals");
      }}
      aria-label="View New Arrivals & Offers"
      data-testid="advertisement-banner"
    >
      <Carousel setApi={setApi} className="h-full" opts={{ loop: true }}>
        <CarouselContent className="h-full ml-0">
          {images.map((url, i) => (
            <CarouselItem key={i} className="pl-0 h-full">
              <div className="relative w-full h-full">
                <MediaTile src={url} mediaType="image" alt={`Advertisement ${i + 1}`} className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="absolute inset-0 bg-gradient-to-t from-background/50 via-transparent to-transparent pointer-events-none" />

      <div className="absolute bottom-3 right-4 sm:bottom-4 sm:right-6 rounded-full bg-background/85 backdrop-blur px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-foreground border border-primary/30 opacity-90 group-hover:opacity-100 transition-opacity">
        See New Arrivals &amp; Offers &rarr;
      </div>

      {images.length > 1 && (
        <div className="absolute bottom-3 left-4 sm:bottom-4 sm:left-6 flex gap-1.5">
          {images.map((_, i) => (
            <span key={i} className="h-1.5 w-1.5 rounded-full bg-background/70" />
          ))}
        </div>
      )}
    </section>
  );
};
